// ==========================================
// Roll Towers - TypeScript Types
// ==========================================

// Типы роллов
export type RollVariant = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type RollType = 'normal' | 'hot';
export type RollColor = 'red' | 'green' | 'red-green' | 'yellow' | 'green-yellow' | 'hot-1' | 'hot-2';

// Размеры роллов — все одинаковые (множитель для стекинга в башне)
// Variant → SVG: 1→avocado, 2→rice-ginger, 3→salmon, 4→ikura, 5→cucumber, 6→hot-dragon, 7→hot-tamago
export const ROLL_SIZES: Record<RollVariant, number> = {
  1: 1.0,    // Авокадо
  2: 1.0,    // Рис-имбирь
  3: 1.0,    // Лосось
  4: 1.0,    // Икра
  5: 1.0,    // Огурец
  6: 1.0,    // Горячий дракон
  7: 1.0,    // Горячий тамаго
};

// Размеры роллов для отображения (80% от SVG viewBox из Figma)
// Используются для корректного отображения aspect ratio
export const ROLL_DIMENSIONS: Record<RollVariant, { width: number; height: number }> = {
  1: { width: 64, height: 68 },   // Авокадо (SVG: 80×85)
  2: { width: 68, height: 68 },   // Рис-имбирь (SVG: 85×85)
  3: { width: 82, height: 69 },   // Лосось (SVG: 102×86)
  4: { width: 68, height: 68 },   // Икра (SVG: 85×85)
  5: { width: 68, height: 68 },   // Огурец (SVG: 85×85)
  6: { width: 72, height: 71 },   // Горячий дракон (SVG: 90×89)
  7: { width: 70, height: 74 },   // Горячий тамаго (SVG: 87×93)
};

export const ROLL_COLORS: Record<RollVariant, RollColor> = {
  1: 'red',
  2: 'green',
  3: 'red-green',
  4: 'yellow',
  5: 'green-yellow',
  6: 'hot-1',
  7: 'hot-2',
};

// Ролл в башне
export interface TowerRoll {
  id: string;
  variant: RollVariant;
  type: RollType;
  positionX: number;      // Позиция X в пикселях от центра (с учётом физики сползания)
  coefficient: number;    // Полученный коэффициент
  isBonus: boolean;       // Бонусный ролл (каждый 10-й)
}

// Бонусы в магазине
export type BonusType = 'hot_chance_10' | 'hot_chance_30' | 'hot_chance_50' | 'stability';

export interface ShopBonus {
  type: BonusType;
  name: string;
  description: string;
  costPercent: number;    // Процент от текущей ставки
}

export const SHOP_BONUSES: ShopBonus[] = [
  {
    type: 'hot_chance_10',
    name: 'HOT +10%',
    description: 'Увеличивает шанс HOT ROLLS на 10%',
    costPercent: 10,
  },
  {
    type: 'hot_chance_30',
    name: 'HOT +30%',
    description: 'Увеличивает шанс HOT ROLLS на 30%',
    costPercent: 30,
  },
  {
    type: 'hot_chance_50',
    name: 'HOT +50%',
    description: 'Увеличивает шанс HOT ROLLS на 50%',
    costPercent: 50,
  },
  {
    type: 'stability',
    name: 'Устойчивость',
    description: 'Убирает проигрыш при краевом промахе',
    costPercent: 25,
  },
];

// Состояние игры
export type GameStatus = 'idle' | 'betting' | 'playing' | 'hot_rolls' | 'game_over' | 'cashed_out';

export interface GameState {
  status: GameStatus;
  balance: number;
  currentBet: number;
  currentStake: number;       // Ставка после применения коэффициентов
  tower: TowerRoll[];
  isHotRollsActive: boolean;
  hotRollsEndTime: number | null;
  activeBonus: BonusType | null;
  lastCoefficient: number | null;
}

// API типы
export interface GameSession {
  sessionId: string;
  userId: string;
  initialBet: number;
  currentStake: number;
  rollsPlaced: number;
  coefficients: number[];
  isHotRollsActive: boolean;
  hotRollsEndTime?: number;
  activeBonus?: BonusType;
  status: 'active' | 'won' | 'lost';
}

export interface DropResponse {
  rollType: RollType;
  rollVariant: RollVariant;
  rollSize: number;
  centerOffset: number;       // 0-100+ процент смещения
  coefficient: number;
  newStakeValue: number;
  isGameOver: boolean;
  isBonusRoll: boolean;
  triggerHotRolls?: boolean;
}

export interface GameHistory {
  gameId: string;
  date: Date;
  bet: number;
  result: number;
  rollsCount: number;
  maxCoefficient: number;
  status: 'win' | 'loss';
}

// Конфигурация игры
export const GAME_CONFIG = {
  MIN_BET: 10,                // 0.10 major units with precision=2
  MAX_BET: 5000000,           // 50,000 major units with precision=2
  RTP: 0.95,                  // 95%
  BONUS_LINE_INTERVAL: 10,    // Каждые 10 роллов
  HOT_ROLLS_DURATION: 5000,   // 5 секунд
  TOWER_MAX_VISIBLE: 5,       // Максимум роллов в DOM (буфер для плавного скролла)
  TOWER_OVERLAP: 0.95,        // Коэффициент перекрытия роллов (0.95 = роллы перекрываются на 5%)
  DROP_ANIMATION_TIME: 800,    // мс — время падения (800ms)
  NEW_ROLL_DELAY: 1000,       // 1-1.5 сек до нового ролла
  
  // Скорость палочек (плавнее)
  CHOPSTICKS_SPEED_NORMAL: 1.8, // секунды на цикл (замедлено для плавности)
  CHOPSTICKS_SPEED_HOT: 1.3,   // Быстрее нормы, но не x2 (было 0.9)
  CHOPSTICKS_AMPLITUDE_NORMAL: 30, // %
  CHOPSTICKS_AMPLITUDE_HOT: 40,    // +20%
} as const;

// Таблица коэффициентов
export const COEFFICIENT_TABLE: Array<{
  minOffset: number;
  maxOffset: number;
  minCoef: number;
  maxCoef: number;
}> = [
  { minOffset: 0, maxOffset: 0, minCoef: 1.5, maxCoef: 2.0 },
  { minOffset: 1, maxOffset: 10, minCoef: 1.25, maxCoef: 1.75 },
  { minOffset: 11, maxOffset: 20, minCoef: 1.0, maxCoef: 1.5 },
  { minOffset: 21, maxOffset: 30, minCoef: 0.75, maxCoef: 1.25 },
  { minOffset: 31, maxOffset: 40, minCoef: 0.5, maxCoef: 1.0 },
  { minOffset: 41, maxOffset: 50, minCoef: 0.25, maxCoef: 1.0 },
  { minOffset: 51, maxOffset: 60, minCoef: 0.25, maxCoef: 0.75 },
  { minOffset: 61, maxOffset: 70, minCoef: 0.1, maxCoef: 0.75 },
  { minOffset: 71, maxOffset: 80, minCoef: 0.1, maxCoef: 0.5 },
  { minOffset: 81, maxOffset: 90, minCoef: 0.1, maxCoef: 0.25 },
  { minOffset: 91, maxOffset: 100, minCoef: 0.1, maxCoef: 0.1 },
];




